const authHeaders = () => ({
    Authorization: "Bearer " + sessionStorage.getItem("ZeitroA"),
    Cache: "no-cache",
    "Content-Type": "application/json",
})

const parseJson = async (response) => {
    const raw = await response.text()
    let body
    try {
        body = JSON.parse(raw)
    } catch {
        const firstObj = raw.indexOf("{")
        const lastObj = raw.lastIndexOf("}")
        const firstArr = raw.indexOf("[")
        const lastArr = raw.lastIndexOf("]")
        const hasObject = firstObj >= 0 && lastObj > firstObj
        const hasArray = firstArr >= 0 && lastArr > firstArr
        const start = hasObject ? firstObj : firstArr
        const end = hasObject ? lastObj : lastArr
        if (start >= 0 && end > start) {
            body = JSON.parse(raw.slice(start, end + 1))
        } else {
            throw new Error(`Invalid JSON response (${response.status})`)
        }
    }
    if (!response.ok || !body || body.Status !== "OK") {
        throw new Error(body?.Message || body?.Text || `request failed (${response.status})`)
    }
    return body
}

const serializeChosenLoan = (loan) => {
    if (loan == null) return {}
    if (typeof loan.toJson === "function") {
        return JSON.parse(loan.toJson())
    }
    if (typeof loan !== "object" || Array.isArray(loan)) return loan
    const out = {}
    Object.keys(loan).forEach((key) => {
        const dest = key.startsWith("_") ? key.slice(1) : key
        out[dest] = loan[key]
    })
    return out
}

export const getLosApplicationV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/application`, {
        method: "GET",
        headers: authHeaders(),
    })
    return parseJson(response)
}

export const addLosCoborrowerV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/coborrowers`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({}),
    })
    return parseJson(response)
}

export const deleteLosCoborrowerV2 = async (loanId, partyId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/participants/${partyId}`, {
        method: "DELETE",
        headers: authHeaders(),
    })
    return parseJson(response)
}

const patchParticipant = async (loanId, partyId, path, payload) => {
    const response = await fetch(`/los/v2/loans/${loanId}/participants/${partyId}/${path}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(payload),
    })
    return parseJson(response)
}

export const patchParticipantProfileV2 = (loanId, partyId, payload) =>
    patchParticipant(loanId, partyId, "profile", payload)

export const patchParticipantResidenceV2 = (loanId, partyId, payload) =>
    patchParticipant(loanId, partyId, "residence", payload)

export const patchParticipantEmploymentV2 = (loanId, partyId, payload) =>
    patchParticipant(loanId, partyId, "employment", payload)

export const patchParticipantOtherIncomeV2 = (loanId, partyId, payload) =>
    patchParticipant(loanId, partyId, "other-income", payload)

export const patchParticipantAssetsV2 = (loanId, partyId, assets) =>
    patchParticipant(loanId, partyId, "assets", { assets })

export const patchParticipantLiabilitiesV2 = (loanId, partyId, liabilities) =>
    patchParticipant(loanId, partyId, "liabilities", { liabilities })

export const postLosCreditImportV2 = async (loanId, body) => {
    const response = await fetch(`/los/v2/loans/${loanId}/credit-import`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
    })
    return parseJson(response)
}

export const postLosParticipantCreditPullV2 = async (loanId, partyId, body) => {
    const response = await fetch(`/los/v2/loans/${loanId}/participants/${partyId}/credit-pull`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
    })
    return parseJson(response)
}

export const postLosCreditPullPaymentRequestV2 = async (loanId, body) => {
    const response = await fetch(`/los/v2/loans/${loanId}/credit-pull/payment-request`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
    })
    return parseJson(response)
}

export const getLosCreditPullPaymentRequestV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/credit-pull/payment-request`, {
        method: "GET",
        headers: authHeaders(),
    })
    return parseJson(response)
}

export const deleteLosCreditPullPaymentRequestV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/credit-pull/payment-request`, {
        method: "DELETE",
        headers: authHeaders(),
    })
    return parseJson(response)
}

export const patchParticipantDeclarationsV2 = (loanId, partyId, declarations, step = "") =>
    patchParticipant(loanId, partyId, "declarations", { declarations, step })

export const patchParticipantRealEstateV2 = (loanId, partyId, realEstate) =>
    patchParticipant(loanId, partyId, "real-estate", { realEstate })

export const patchParticipantSsnV2 = (loanId, partyId, socialSecurity) =>
    patchParticipant(loanId, partyId, "ssn", { socialSecurity })

export const patchOccupationCurrentV2 = (loanId, partyId, occupation) =>
    patchParticipant(loanId, partyId, "occupation-current", { occupation })

export const patchPresentAddressV2 = (loanId, partyId, payload) =>
    patchParticipant(loanId, partyId, "present-address", payload)

export const patchOwnedPropertyFieldsV2 = (loanId, partyId, index, fields) =>
    patchParticipant(loanId, partyId, "owned-property-fields", { index, fields })

export const patchIncomeFromCalculationV2 = (loanId, partyId, payload) =>
    patchParticipant(loanId, partyId, "income-from-calculation", payload)

const patchLoan = async (loanId, path, payload) => {
    const response = await fetch(`/los/v2/loans/${loanId}/${path}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(payload),
    })
    return parseJson(response)
}

export const patchPropertyFieldsV2 = (loanId, fields) =>
    patchLoan(loanId, "property-fields", { fields })

export const patchPropertyV2 = (loanId, payload) => patchLoan(loanId, "property", payload)

export const patchSelectionV2 = (loanId, payload) => patchLoan(loanId, "selection", payload)

export const patchLoanFieldsV2 = (loanId, payload) => patchLoan(loanId, "loan-fields", payload)

export const patchBrokerInfoV2 = (loanId, brokerInfo) =>
    patchLoan(loanId, "broker-info", { brokerInfo })

export const getLosLenderLoanFormV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/lender-loan-form`, {
        method: "GET",
        headers: authHeaders(),
    })
    return parseJson(response)
}

export const patchLosLenderLoanFormV2 = async (loanId, dataType, data) => {
    const response = await fetch(`/los/v2/loans/${loanId}/lender-loan-form`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ dataType, data }),
    })
    return parseJson(response)
}

export const patchLosRecommendedRatesV2 = async (loanId, loSelectedLoans) => {
    const response = await fetch(`/los/v2/loans/${loanId}/selection/recommended-rates`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({
            loSelectedLoans: (loSelectedLoans || []).map(serializeChosenLoan),
        }),
    })
    return parseJson(response)
}

export const patchLosSelectedLoanV2 = async (loanId, selectedloan) => {
    const response = await fetch(`/los/v2/loans/${loanId}/selection/selected-loan`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ selectedloan: serializeChosenLoan(selectedloan) }),
    })
    return parseJson(response)
}

export const sendLosPrequalLetterV2 = async (loanId, payload) => {
    const response = await fetch(`/los/v2/loans/${loanId}/prequal-letter`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
    })
    return parseJson(response)
}

export const getLosEscrowV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/escrow`, {
        method: "GET",
        headers: authHeaders(),
    })
    return parseJson(response)
}

export const patchLosEscrowV2 = async (loanId, payload) => {
    const response = await fetch(`/los/v2/loans/${loanId}/escrow`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(payload),
    })
    return parseJson(response)
}

export const getLosAppraisalV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/appraisal`, {
        method: "GET",
        headers: authHeaders(),
    })
    return parseJson(response)
}

export const patchLosAppraisalV2 = async (loanId, payload) => {
    const response = await fetch(`/los/v2/loans/${loanId}/appraisal`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(payload),
    })
    return parseJson(response)
}

export const getLosDTIV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/dti`, {
        method: "GET",
        headers: authHeaders(),
    })
    return parseJson(response)
}

/** Partial update: { dtiRate?, interestOnly?, OverallDTI?: { OverallDTIIncome?, OverallDTILiability?, ... } } */
export const patchLosDTIV2 = async (loanId, payload) => {
    const response = await fetch(`/los/v2/loans/${loanId}/dti`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(payload),
    })
    return parseJson(response)
}

export const getLosDocumentAutotasksV2 = async (loanId) => {
    const response = await fetch(`/los/v2/loans/${loanId}/document-autotasks/suggestions`, {
        method: "GET",
        headers: authHeaders(),
    })
    return parseJson(response)
}
